import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Chip, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem,
  Radio, RadioGroup, Select, Snackbar, TextField, Typography,
} from '@mui/material';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  allDescriptionKeys, annotationsApi, selectAnnotations, selectFilterableFields, updateAnnotations,
} from '../app/annotations.js';
import { filterAnnotationsForNumbering, buildNumberingPlan, getShapePoints } from '../app/annotationNumbering.js';
import { runInParallel } from '../app/runInParallel.js';

/** Nombre d'appels API editAnnotations lancés en même temps (1 annotation par appel). */
const MAX_PARALLEL = 6;

/**
 * Délai avant de pousser les annotations mises à jour dans Redux et de rafraîchir le message
 * de progression. Évite un re-render de toute la carte à chaque numéro enregistré.
 */
const STORE_DELAY_MS = 200;

/** Style des panneaux « Filters » et « Numbering » dans la modale. */
const panelSx = { p: 2, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' };

/** Style d'une ligne de filtre actif (Include/Exclude + champ = valeur). */
const rowSx = { display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1.25, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' };

const summaryText = (n, total, start, hasFilters) => {
  const s = Number(start) || 1;
  if (!n) return total ? (hasFilters ? 'No annotations match the filters.' : 'Nothing to number.') : 'No annotations with geometry.';
  const end = s + n - 1;
  const count = n < total ? `${n} of ${total} annotations` : `${n} annotation${n > 1 ? 's' : ''}`;
  return `${count} will be numbered ${s === end ? s : `${s} to ${end}`}.`;
};

const LabeledCheckbox = ({ label, checked, onChange }) => (
  <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, mb: 0.25 }}>{label}</Typography>
    <Checkbox size="small" checked={checked} onChange={onChange} sx={{ p: 0.5 }} />
  </Box>
);

/** Ligne de tri : direction (gauche/droite ou haut/bas) + case Priority pour cette axe. */
const NumberingSortRow = ({ title, value, options, onChange, priorityOn, prioritySet }) => (
  <Box sx={{ ...panelSx, bgcolor: 'background.paper', py: 1.25, px: 1.5, mb: title === 'Horizontal' ? 1 : 0 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>{title}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
      <RadioGroup row value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map(([v, l]) => <FormControlLabel key={v} value={v} control={<Radio size="small" />} label={l} />)}
      </RadioGroup>
      <Box sx={{ ml: 'auto' }}>
        <LabeledCheckbox label="Priority" checked={priorityOn} onChange={() => prioritySet()} />
      </Box>
    </Box>
  </Box>
);

async function runNumbering({ dispatch, api, inspectionId, plan, field, setSnackbar, setRunning }) {
  const total = plan.length;
  let doneCount = 0;
  /** Annotations déjà sauvées par l'API, pas encore envoyées au store Redux. */
  let savedButNotInStore = [];
  /** Timer pour regrouper les dispatch Redux (voir STORE_DELAY_MS). */
  let storeUpdateTimer = null;
  /** Timer pour ne pas mettre à jour le Snackbar à chaque annotation. */
  let progressMessageTimer = null;

  const applyStoreUpdate = () => {
    storeUpdateTimer = null;
    if (!savedButNotInStore.length) return;
    dispatch(updateAnnotations(savedButNotInStore));
    savedButNotInStore = [];
  };
  /** Regroupe les dispatch Redux : un seul update toutes les STORE_DELAY_MS. */
  const scheduleStoreUpdate = () => {
    if (!storeUpdateTimer) storeUpdateTimer = setTimeout(applyStoreUpdate, STORE_DELAY_MS);
  };
  /** Met à jour le Snackbar de progression sans le faire à chaque annotation. */
  const scheduleProgressMessage = () => {
    if (!progressMessageTimer) {
      progressMessageTimer = setTimeout(() => {
        progressMessageTimer = null;
        setSnackbar({ open: true, message: `Numbering ${doneCount} / ${total}…`, severity: 'info' });
      }, STORE_DELAY_MS);
    }
  };
  setRunning(true);
  setSnackbar({ open: true, message: `Numbering 0 / ${total}…`, severity: 'info' });
  try {
    await runInParallel(plan, MAX_PARALLEL, ({ id, number }) => new Promise((ok, ko) => {
      api({ ids: [id], data: { [field]: number }, inspectionId }, (res) => {
        if (!res) ko(new Error('fail'));
        else {
          savedButNotInStore.push(...res);
          scheduleStoreUpdate();
          doneCount++;
          scheduleProgressMessage();
          ok(res);
        }
      });
    }));
    if (storeUpdateTimer) clearTimeout(storeUpdateTimer);
    applyStoreUpdate();
    setSnackbar({ open: true, message: `Numbered ${total} annotation(s).`, severity: 'success' });
  } catch (e) {
    console.error(e);
    if (storeUpdateTimer) clearTimeout(storeUpdateTimer);
    applyStoreUpdate();
    setSnackbar({
      open: true,
      message: doneCount ? `Numbering stopped after ${doneCount} / ${total}.` : 'Numbering failed.',
      severity: 'error',
    });
  } finally {
    if (progressMessageTimer) clearTimeout(progressMessageTimer);
    setRunning(false);
  }
}

function AnnotationNumberingModal({ numberFields }) {
  const [searchParams] = useSearchParams();
  const inspectionId = searchParams.get('inspectionId');
  const dispatch = useDispatch();
  const annotations = useSelector(selectAnnotations);
  const viewKeys = useSelector(allDescriptionKeys);
  const fields = useSelector(selectFilterableFields);
  const api = useSelector(annotationsApi.update);

  const [open, setOpen] = useState(false);
  const [verticalStart, setVerticalStart] = useState('top');
  const [horizontalStart, setHorizontalStart] = useState('left');
  const [sortPriority, setSortPriority] = useState('vertical');
  const [fieldName, setFieldName] = useState('');
  const [startNum, setStartNum] = useState(1);
  const [filters, setFilters] = useState([]);
  const [draft, setDraft] = useState({ fieldKey: '', fieldValue: '', exclude: false });
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

  const draftOpts = fields.find((f) => f.name === draft.fieldKey)?.options ?? [];
  const withShape = useMemo(() => annotations.filter((a) => getShapePoints(a.geometry).length > 0), [annotations]);
  const toNumber = useMemo(() => filterAnnotationsForNumbering(withShape, viewKeys, filters), [withShape, viewKeys, filters]);
  const planned = useMemo(
    () => buildNumberingPlan(toNumber, verticalStart, horizontalStart, Number(startNum) || 1, sortPriority).length,
    [toNumber, verticalStart, horizontalStart, startNum, sortPriority]
  );
  const summary = useMemo(() => summaryText(planned, withShape.length, startNum, filters.length > 0), [planned, withShape.length, startNum, filters.length]);

  const addFilter = () => {
    if (!draft.fieldKey || !draft.fieldValue) return;
    const mode = draft.exclude ? 'exclude' : 'include';
    setFilters((f) => [...f, { id: `${draft.fieldKey}-${draft.fieldValue}-${Date.now()}`, fieldKey: draft.fieldKey, fieldValue: draft.fieldValue, mode }]);
    setDraft({ fieldKey: '', fieldValue: '', exclude: false });
  };

  const apply = () => {
    const start = Number(startNum);
    if (!inspectionId || !fieldName || running || !Number.isFinite(start)) {
      if (!Number.isFinite(start)) setError('Start number must be a valid number.');
      return;
    }
    const plan = buildNumberingPlan(toNumber, verticalStart, horizontalStart, start, sortPriority);
    if (!plan.length) {
      setError(filters.length ? 'No annotations match filters.' : 'No annotations with geometry.');
      return;
    }
    setError(null);
    setOpen(false);
    void runNumbering({ dispatch, api, inspectionId, plan, field: fieldName, setSnackbar: setSnack, setRunning });
  };

  const off = !numberFields.length || running;

  return (
    <>
      <button type="button" disabled={off} title={running ? 'In progress…' : off ? 'No number field' : 'Number annotations'}
        onClick={() => { setError(null); if (numberFields[0] && !fieldName) setFieldName(numberFields[0].name); setOpen(true); }}>
        {running ? <CircularProgress size={18} sx={{ mr: 0.5, verticalAlign: 'middle' }} /> : <FormatListNumberedIcon />} Number
      </button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { maxHeight: 'min(90vh, 540px)', display: 'flex', flexDirection: 'column' } }}>
        <DialogTitle>Number annotations</DialogTitle>
        <DialogContent dividers sx={{ px: 3, py: 2.5, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {!numberFields.length ? (
            <Typography color="text.secondary">No number field in this view.</Typography>
          ) : (
            <>
              <Typography variant="body2" color={planned ? 'text.primary' : 'text.secondary'} sx={{ mb: 2.5 }}>{summary}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <Box sx={{ ...panelSx, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>Filters</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mb: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'background.paper', border: '1px dashed', borderColor: 'divider' }}>
                    <LabeledCheckbox label="Exclude" checked={draft.exclude} onChange={(e) => setDraft((d) => ({ ...d, exclude: e.target.checked }))} />
                    <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                      <InputLabel>Field</InputLabel>
                      <Select label="Field" value={draft.fieldKey} onChange={(e) => setDraft((d) => ({ ...d, fieldKey: e.target.value, fieldValue: '' }))}>
                        {fields.map((f) => <MenuItem key={f.name} value={f.name}>{f.title || f.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ flex: 1, minWidth: 0 }} disabled={!draft.fieldKey}>
                      <InputLabel>Value</InputLabel>
                      <Select label="Value" value={draft.fieldValue} onChange={(e) => setDraft((d) => ({ ...d, fieldValue: e.target.value }))}>
                        {draftOpts.map((o) => <MenuItem key={o.id} value={o.id}>{o.text}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <Button variant="outlined" size="small" onClick={addFilter} disabled={!draft.fieldKey || !draft.fieldValue} sx={{ height: 40, minWidth: 56 }}>Add</Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: filters.length ? 112 : 0, overflowY: filters.length ? 'auto' : 'hidden' }}>
                    {filters.map((r) => {
                      const fld = fields.find((f) => f.name === r.fieldKey);
                      const lbl = fld?.options?.find((o) => o.id === r.fieldValue)?.text ?? r.fieldValue;
                      const inc = r.mode === 'include';
                      return (
                        <Box key={r.id} sx={rowSx}>
                          <Chip size="small" label={inc ? 'Include' : 'Exclude'} color={inc ? 'primary' : 'default'} variant={inc ? 'filled' : 'outlined'} sx={{ flexShrink: 0 }} />
                          <Typography variant="body2" noWrap title={`${fld?.title || r.fieldKey} = ${lbl}`} sx={{ flex: 1, minWidth: 0 }}>{fld?.title || r.fieldKey} = {lbl}</Typography>
                          <IconButton size="small" onClick={() => setFilters((f) => f.filter((x) => x.id !== r.id))}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
                <Box sx={panelSx}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>Numbering</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Number field</InputLabel>
                      <Select label="Number field" value={fieldName || numberFields[0]?.name || ''} onChange={(e) => setFieldName(e.target.value)}>
                        {numberFields.map((f) => <MenuItem key={f.name} value={f.name}>{f.title || f.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField size="small" fullWidth type="number" label="First number" value={startNum} onChange={(e) => setStartNum(e.target.value)} inputProps={{ step: 1 }} />
                  </Box>
                  <NumberingSortRow title="Horizontal" value={horizontalStart} options={[['left', 'Left'], ['right', 'Right']]} onChange={setHorizontalStart} priorityOn={sortPriority === 'horizontal'} prioritySet={() => setSortPriority('horizontal')} />
                  <NumberingSortRow title="Vertical" value={verticalStart} options={[['top', 'Top'], ['bottom', 'Bottom']]} onChange={setVerticalStart} priorityOn={sortPriority === 'vertical'} prioritySet={() => setSortPriority('vertical')} />
                </Box>
              </Box>
              {error && <Typography color="error" sx={{ mt: 1.5 }}>{error}</Typography>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={apply} disabled={!numberFields.length || !planned}>Apply numbering</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={running ? null : 6000}
        onClose={(_, r) => { if (r !== 'clickaway' || !running) setSnack((s) => ({ ...s, open: false })); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} variant="filled" sx={{ width: '100%' }}>{snack.message}</Alert>
      </Snackbar>
    </>
  );
}

export default AnnotationNumberingModal;
